import { useState, useEffect } from 'react';
import { api } from '../api';
import BarcodeScanner from './BarcodeScanner';
import ImageManager from './ImageManager';
import { usePremium } from '../contexts/PremiumContext';
import PaywallModal from './PaywallModal';
import AllergenSection from './AllergenSection';
import { scanTextOCR, isIOS } from '../native';
import { parseAllergens } from '../utils/allergenParser';
import { useTranslation } from 'react-i18next';

export default function AddItemModal({ listType, locations, storageUnits = [], onClose, onSaved }) {
  const { t } = useTranslation();
  const [step, setStep] = useState('search'); // search | new | confirm
  const [kbHeight, setKbHeight] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [search, setSearch] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);

  const [form, setForm] = useState({
    product_id: null, name: '', brand: '', barcode: '',
    category: listType, unit: 'stk', weight_per_unit: '',
    quantity: 1, location_id: '',
    expiry_date: listType === 'freezer' ? (() => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); return d.toISOString().slice(0, 10); })() : '',
    date_added: new Date().toISOString().slice(0, 10),
    opened: false,
    notes: '', thumbnail: '',
    allergens: [], traces: [], labels: [], ingredients_text: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [ocrScanning, setOcrScanning] = useState(false);
  const [localImage, setLocalImage] = useState(null);
  const { isPremium } = usePremium();
  const [showPaywall, setShowPaywall] = useState(false); // { file, preview }

  useEffect(() => { api.getCatalog().then(setCatalog); }, []);

  useEffect(() => {
    if (!isIOS()) return;
    let l1, l2, l3, l4;
    import(/* @vite-ignore */ '@capacitor/keyboard').then(({ Keyboard }) => {
      Keyboard.addListener('keyboardWillShow', info => setKbHeight(info.keyboardHeight)).then(l => { l1 = l; });
      Keyboard.addListener('keyboardDidShow',  info => setKbHeight(info.keyboardHeight)).then(l => { l3 = l; });
      Keyboard.addListener('keyboardWillHide', () => setKbHeight(0)).then(l => { l2 = l; });
      Keyboard.addListener('keyboardDidHide',  () => setKbHeight(0)).then(l => { l4 = l; });
    }).catch(() => {});
    return () => { l1?.remove(); l2?.remove(); l3?.remove(); l4?.remove(); };
  }, []);

  const filteredCatalog = catalog.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.brand || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.barcode || '').includes(search)
  );

  const handleBarcode = async (code) => {
    setScanning(false);
    setLookupLoading(true);
    try {
      const { source, product } = await api.lookupBarcode(code);
      if (source === 'local') {
        selectFromCatalog(product);
      } else if (product) {
        setForm(f => ({ ...f, ...product, barcode: code }));
        setStep('new');
      } else {
        setForm(f => ({ ...f, barcode: code }));
        setStep('new');
      }
    } catch {
      // Lookup fejlede — vis stadig formular med stregkoden
      setForm(f => ({ ...f, barcode: code }));
      setStep('new');
    } finally { setLookupLoading(false); }
  };

  const runOCR = async () => {
    setOcrScanning(true);
    try {
      const text = await scanTextOCR();
      if (!text) return;
      const { allergens, labels } = parseAllergens(text);
      setForm(prev => ({
        ...prev,
        allergens: [...new Set([...prev.allergens, ...allergens])],
        labels:    [...new Set([...prev.labels, ...labels])],
        ingredients_text: prev.ingredients_text || text,
      }));
    } finally { setOcrScanning(false); }
  };

  const selectFromCatalog = (product) => {
    setForm(f => ({
      ...f,
      product_id: product.id,
      name: product.name,
      brand: product.brand || '',
      unit: product.unit || 'stk',
      weight_per_unit: product.weight_per_unit || '',
      location_id: product.default_location_id || '',
      category: product.category || listType,
      thumbnail: product.thumbnail || '',
    }));
    setStep('confirm');
  };

  const save = async () => {
    if (!form.name && !form.product_id) return;
    // Tjek om lokationen er i låst enhed
    if (form.location_id && !isPremium) {
      const loc = locations.find(l => l.id == form.location_id);
      const unit = storageUnits.find(u => u.id === loc?.storage_unit_id);
      if (unit && unit.position > 0) { setShowPaywall(true); return; }
    }
    setSaving(true);
    setSaveError(null);
    try {
      let productId = form.product_id;
      if (!productId) {
        const product = await api.createProduct({
          name: form.name, brand: form.brand || null,
          barcode: form.barcode || null,
          category: listType, unit: form.unit,
          weight_per_unit: form.weight_per_unit ? parseFloat(form.weight_per_unit) : null,
          thumbnail: form.thumbnail || null,
          allergens: form.allergens,
          traces: form.traces,
          labels: form.labels,
          ingredients_text: form.ingredients_text || null,
        });
        productId = product.id;
        if (localImage?.file) await api.uploadImage(productId, localImage.file);
      }
      await api.addInventory({
        product_id: productId,
        quantity: parseInt(form.quantity) || 1,
        list_type: listType,
        location_id: form.location_id || null,
        expiry_date: form.expiry_date || null,
        date_added: form.date_added || null,
        notes: form.notes || null,
        opened: form.opened ? 1 : 0,
        opened_at: form.opened ? new Date().toISOString().slice(0, 10) : null,
      });
      onSaved();
    } catch (err) {
      setSaveError(err?.message || t('addItem.genericError'));
    } finally { setSaving(false); }
  };

  const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const scrollToInput = (e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);

  return (
    <>
      {scanning && <BarcodeScanner onScan={handleBarcode} onClose={() => setScanning(false)} />}

      {/* Full-screen overlay on iOS to avoid keyboard clipping */}
      <div className="fixed inset-0 z-40 bg-black/60 flex flex-col justify-end" onClick={onClose}>
        <div
          className="ft-sheet flex flex-col" style={{ borderRadius: "20px 20px 0 0", maxHeight: isIOS() && kbHeight ? `calc(100vh - ${kbHeight}px - 60px)` : "92dvh", minHeight: isIOS() && kbHeight ? 0 : "60dvh", marginBottom: isIOS() ? kbHeight || 0 : 0 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-1">
            <div className="ft-sheet-handle" />
          </div>

          {/* Sticky header */}
          <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
            <div className="flex items-center gap-2">
              {step !== 'search' && (
                <button onClick={() => setStep('search')} className="text-blue-400 text-sm mr-1">{t('add.back')}</button>
              )}
              <h2 className="font-bold text-lg">
                {step === 'search' ? t('add.title') : step === 'new' ? t('add.newProduct') : t('add.addToStock')}
              </h2>
            </div>
            <button onClick={onClose} className="text-slate-400 text-3xl leading-none w-10 h-10 flex items-center justify-center">×</button>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto flex-1 p-4 space-y-4" style={{ WebkitOverflowScrolling: 'touch', overflowX: 'hidden', paddingBottom: !isIOS() && kbHeight ? `calc(${kbHeight}px + env(safe-area-inset-bottom) + 32px)` : 'calc(12rem + env(safe-area-inset-bottom))' }}>

            {/* ── STEP: SEARCH ── */}
            {step === 'search' && (
              <>
                <button onClick={() => setScanning(true)}
                  className="w-full py-3.5 rounded-2xl bg-blue-600 text-white font-semibold flex items-center justify-center gap-2 active:bg-blue-700 text-base">
                  {t('add.scanBarcode')}
                </button>
                {lookupLoading && <p className="text-center text-slate-400 animate-pulse text-sm">{t('add.lookingUp')}</p>}

                <input
                  type="text" placeholder={t('add.searchPlaceholder')}
                  value={search} onChange={e => setSearch(e.target.value)}
                  className="w-full bg-slate-700 rounded-2xl px-4 py-3.5 text-white placeholder-slate-400 outline-none text-base border border-slate-600 focus:border-blue-500"
                  autoFocus
                />

                {search.length > 0 && (
                  <button
                    onClick={() => { setForm(f => ({ ...f, name: search })); setStep('new'); }}
                    className="w-full py-3.5 rounded-2xl bg-slate-700 text-blue-400 font-semibold border border-slate-600 active:bg-slate-600 text-base">
                    {t('add.createNew', { name: search })}
                  </button>
                )}

                <div className="space-y-2">
                  {filteredCatalog.slice(0, 30).map(p => (
                    <button key={p.id} onClick={() => selectFromCatalog(p)}
                      className="w-full flex items-center gap-3 bg-slate-700 rounded-2xl px-3 py-3 text-left active:bg-slate-600">
                      <div className="w-11 h-11 rounded-xl bg-slate-600 flex-shrink-0 overflow-hidden">
                        {p.thumbnail
                          ? <img src={p.thumbnail} alt="" className="w-full h-full object-cover" />
                          : <span className="w-full h-full flex items-center justify-center text-xl">📦</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{p.name}</p>
                        <p className="text-xs text-slate-400">{[p.brand, p.weight_per_unit && `${p.weight_per_unit}g`].filter(Boolean).join(' · ')}</p>
                      </div>
                      {p.in_stock > 0 && (
                        <span className="text-xs bg-green-900 text-green-300 px-2 py-0.5 rounded-full flex-shrink-0">{t('add.inStock', { count: p.in_stock })}</span>
                      )}
                    </button>
                  ))}
                </div>
                {/* Bottom padding so last item isn't hidden */}
                <div className="h-4" />
              </>
            )}

            {/* ── STEP: NEW PRODUCT ── */}
            {step === 'new' && (
              <>
                <Field label={t('add.name')}>
                  <input value={form.name} onChange={e => f('name', e.target.value)}
                    onFocus={scrollToInput} className="input-field" placeholder={t('add.namePlaceholder')} autoFocus />
                </Field>
                <Field label={t('add.brand')}>
                  <input value={form.brand} onChange={e => f('brand', e.target.value)}
                    onFocus={scrollToInput} className="input-field" placeholder={t('add.brandPlaceholder')} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t('add.unit')}>
                    <select value={form.unit} onChange={e => f('unit', e.target.value)} className="input-field">
                      {['stk','g','kg','ml','l','pk','bk','pose','bakke','flaske','dåse'].map(u => <option key={u} value={u}>{t('units.' + u)}</option>)}
                    </select>
                  </Field>
                  <Field label={t('add.sizeLabel')}>
                    <input value={form.weight_per_unit} onChange={e => f('weight_per_unit', e.target.value)}
                      onFocus={scrollToInput} type="number" className="input-field" placeholder={t('add.sizePlaceholder')} />
                  </Field>
                </div>
                <Field label={t('add.barcodeLabel')}>
                  <div className="flex gap-2">
                    <input value={form.barcode} onChange={e => f('barcode', e.target.value)}
                      onFocus={scrollToInput} className="input-field flex-1" placeholder={t('add.barcodePlaceholder')} />
                    <button onClick={() => setScanning(true)}
                      className="px-4 bg-slate-600 rounded-2xl text-xl active:bg-slate-500">📷</button>
                  </div>
                </Field>
                <Field label={t('add.image')}>
                  <ImageManager
                    productId={null}
                    images={localImage ? [{ id: 'local', preview: localImage.preview, local: true }] : (form.thumbnail ? [{ id: 'thumb', preview: form.thumbnail, local: true }] : [])}
                    onImagesChange={evt => {
                      if (evt.type === 'local') setLocalImage({ file: evt.file, preview: evt.preview });
                      if (evt.type === 'delete-local') { setLocalImage(null); f('thumbnail', ''); }
                    }}
                  />
                </Field>
                <AllergenSection
                  allergens={form.allergens} labels={form.labels}
                  ingredients_text={form.ingredients_text}
                  onAllergenToggle={key => f('allergens', form.allergens.includes(key) ? form.allergens.filter(a => a !== key) : [...form.allergens, key])}
                  onLabelToggle={key => f('labels', form.labels.includes(key) ? form.labels.filter(l => l !== key) : [...form.labels, key])}
                  onIngredientsChange={v => f('ingredients_text', v)}
                  onOCR={runOCR} scanning={ocrScanning} />
                <button onClick={() => setStep('confirm')} disabled={!form.name}
                  className="w-full py-4 rounded-2xl bg-blue-600 text-white font-bold text-base disabled:opacity-40 active:bg-blue-700">
                  {t('add.continue')}
                </button>
                <div className="h-4" />
              </>
            )}

            {/* ── STEP: CONFIRM (antal + placering) ── */}
            {step === 'confirm' && (
              <>
                <div className="bg-slate-700 rounded-2xl p-3 flex items-center gap-3">
                  {(localImage?.preview || form.thumbnail) && (
                    <img src={localImage?.preview || form.thumbnail} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                  )}
                  <div>
                    <p className="font-semibold">{form.name}</p>
                    {form.brand && <p className="text-xs text-slate-400">{form.brand}</p>}
                  </div>
                </div>

                <Field label={t('add.quantity')}>
                  <div className="flex items-center gap-3">
                    <button onClick={() => f('quantity', Math.max(1, form.quantity - 1))}
                      className="w-14 h-14 rounded-2xl bg-slate-700 text-3xl font-bold active:bg-slate-600 flex-shrink-0">−</button>
                    <input value={form.quantity} onChange={e => f('quantity', parseInt(e.target.value) || 1)}
                      onFocus={scrollToInput} type="number" min="1"
                      className="input-field flex-1 text-center text-2xl font-bold" />
                    <button onClick={() => f('quantity', form.quantity + 1)}
                      className="w-14 h-14 rounded-2xl bg-slate-700 text-3xl font-bold active:bg-slate-600 flex-shrink-0">+</button>
                  </div>
                </Field>

                <Field label={t('add.location')}>
                  <select value={form.location_id} onChange={e => f('location_id', e.target.value)} className="input-field">
                    <option value="">{t('add.noLocation')}</option>
                    {storageUnits.length > 0
                      ? storageUnits.map(unit => {
                          const unitLocs = locations.filter(l => l.storage_unit_id === unit.id);
                          if (!unitLocs.length) return null;
                          const locked = unit.position > 0 && !isPremium;
                          return (
                            <optgroup key={unit.id} label={`${locked ? '🔒 ' : ''}${unit.name}`}>
                              {unitLocs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </optgroup>
                          );
                        })
                      : locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)
                    }
                  </select>
                </Field>

                <Field label={listType === 'freezer' ? t('add.freezerDate') : t('add.expiryDate')}>
                  <input type="date" value={form.expiry_date} onChange={e => f('expiry_date', e.target.value)}
                    onFocus={scrollToInput} className="input-field" />
                </Field>

                <Field label={t('add.dateAdded')}>
                  <input type="date" value={form.date_added} onChange={e => f('date_added', e.target.value)}
                    onFocus={scrollToInput} className="input-field" />
                </Field>

                <Field label={t('add.openedLabel')}>
                  <button type="button" onClick={() => f('opened', !form.opened)}
                    className={`w-full py-3 rounded-2xl text-base font-medium transition-colors
                      ${form.opened ? 'bg-amber-600/30 text-amber-400 border border-amber-600' : 'bg-slate-700 text-slate-400 border border-slate-600'}`}>
                    {form.opened ? t('add.markedOpen') : t('add.notOpened')}
                  </button>
                </Field>
                <Field label={t('add.notesLabel')}>
                  <input value={form.notes} onChange={e => f('notes', e.target.value)}
                    onFocus={scrollToInput} className="input-field" placeholder={t('add.notesPlaceholder')} />
                </Field>

                {saveError && (
                  <p className="text-red-400 text-sm text-center px-2">{saveError}</p>
                )}
                {/* Big save button always visible */}
                <button onClick={save} disabled={saving}
                  className="w-full py-4 rounded-2xl bg-blue-600 text-white font-bold text-lg disabled:opacity-40 active:bg-blue-700">
                  {saving ? t('add.saving') : t('add.addButton')}
                </button>
                <div className="h-6" />
              </>
            )}

          </div>
        </div>
      </div>

      <style>{`
        .input-field {
          width: 100%; background: var(--bg-input); border-radius: 12px;
          padding: 12px 14px; color: var(--fg-1); outline: none;
          border: 1px solid var(--border-subtle); font-size: 16px;
          font-family: 'DM Sans', sans-serif;
        }
        .input-field:focus { border-color: var(--brand); }
        .input-field::placeholder { color: var(--fg-4); }
        .input-field option { background: var(--bg-elevated); color: var(--fg-1); }
        .input-field select { background: var(--bg-input); }
      `}</style>
      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} reason={t('paywall.reasonLocation')} />}
    </>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-slate-400">{label}</label>
      {children}
    </div>
  );
}
